import React, { useState, useEffect, useSyncExternalStore } from 'react';
import { UserCircle2, Fish, Crown, Glasses, Gem, Smile, Hand, Settings, Plus, X } from 'lucide-react';
import { VideoEffect, VideoEffectControlProps, VideoEffectCompositeArgs } from './VideoEffect';
import { getAvatarRenderer, getModelThumbnail, HandDecorationOptions, FingerName, FINGER_PIP_LANDMARK } from './AvatarRenderer';
import { AVATAR_MODELS, AvatarModelDef, isHandAnchor, getAvatarModelDef, DecorationTransform, DEFAULT_DECORATION_TRANSFORM } from './AvatarModels';
import { DecorationCalibrationModal } from './DecorationCalibrationModal';
import { ThumbTile } from './ThumbTile';
import { AvatarModelAddModal } from './AvatarModelAddModal';
import { AvatarModelFamily, deleteAvatarModel, subscribeAvatarModels, getAvatarModelsVersion } from './avatarModelStore';
import { LevelSlider } from '../LevelSlider';
import Toggle from '../../../widget/inputs/Toggle';

export interface AvatarConfig {
  /** cf. AvatarModels.ts — id du modèle 3D pour le visage, ou null pour aucun.
   * Indépendant de leftHandModelId/rightHandModelId : les deux peuvent être actifs EN MÊME TEMPS
   * (cf. AvatarRenderer.render, deux familles de slots séparées). */
  faceModelId: string | null;
  faceScale: number;
  /** Idem pour les mains, côté par côté — id d'un modèle ancré 'hand', ou null. La
   * main gauche est celle de la PERSONNE (cf. HandTracker.isPersonLeftHand). */
  leftHandModelId: string | null;
  rightHandModelId: string | null;
  handScale: number;
  /** La personne doit-elle disparaître de la frame (cf. VideoEffect.wantsPersonHidden)
   * pour ne laisser que la tête/main remplacée par le(s) modèle(s) 3D — typiquement
   * combiné avec un effet "Arrière-plan" actif, dont le fond choisi apparaît alors
   * entier à la place du corps (cf. compositeFrame.ts, SurroundingsEffect). */
  hideBody: boolean;
  /** Décorations cochées (cf. AvatarModelDef.isDecoration — Couronne, Lunettes,
   * Bague, Moustache...) — un ENSEMBLE, pas un choix unique : plusieurs peuvent
   * être actives en même temps, entre elles ET avec faceModelId/leftHandModelId/rightHandModelId (cf.
   * AvatarRenderer, un jeu de slots dédié par décoration). */
  decorationIds: string[];
  /** Calibration (cf. DecorationTransform, panneau ⚙ ouvert depuis chaque tuile de
   * décoration) — position/rotation/échelle réglées visuellement plutôt que
   * devinées, une entrée par id de décoration UNE FOIS calibrée (absente = valeur
   * de départ par défaut, cf. AvatarModelDef.defaultTransform). */
  decorationTransforms: Record<string, DecorationTransform>;
  /** Réglages fins de la décoration "Bague" (cf. AvatarRenderer.HandDecorationOptions)
   * — quelle main et quel doigt, plutôt que toutes les mains détectées ancrées au dos
   * de la main. N'a d'effet que si 'ring' figure dans decorationIds. */
  ringHand: 'left' | 'right' | 'both';
  ringFinger: FingerName;
}

// Le registre est rempli après coup, par modèle téléversé (cf. avatarModelStore.ts) :
// on le relit donc à chaque rendu au lieu de le filtrer une fois pour toutes. Le hook
// force le rendu quand la liste change.
function useAvatarModelLists() {
  useSyncExternalStore(subscribeAvatarModels, getAvatarModelsVersion);
  return {
    faceModels : AVATAR_MODELS.filter(m => !isHandAnchor(m.anchor) && !m.isDecoration),
    handModels : AVATAR_MODELS.filter(m => isHandAnchor(m.anchor) && !m.isDecoration),
    decorations: AVATAR_MODELS.filter(m => m.isDecoration),
  };
}

/** Tuile "+" en fin de grille : ouvre la fenêtre d'ajout d'un modèle de cette famille. */
function AddTile({ family, side }: { family: AvatarModelFamily; side?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <ThumbTile active={false} label="Ajouter" onClick={() => setOpen(true)}>
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Plus style={{ width: 26, height: 26, color: '#94a3b8' }} />
        </div>
      </ThumbTile>
      {open && <AvatarModelAddModal family={family} side={side} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Petite croix en haut à gauche d'une tuile : supprime le modèle du serveur. */
function DeleteCorner({ model, onDeleted }: { model: AvatarModelDef; onDeleted: () => void }) {
  return (
    <button
      type="button"
      title={`Supprimer ${model.label}`}
      onClick={async e => {
        e.stopPropagation();
        if (!window.confirm(`Supprimer le modèle « ${model.label} » ?`)) return;
        try {
          await deleteAvatarModel(model.id);
          onDeleted();
        } catch (err) {
          window.alert(err instanceof Error ? err.message : String(err));
        }
      }}
      style={{
        position: 'absolute', top: 2, left: 2, width: 20, height: 20, padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 6, border: '1px solid rgba(148,163,184,0.4)',
        background: 'rgba(15,23,42,0.75)', color: '#f87171', cursor: 'pointer',
      }}
    >
      <X style={{ width: 12, height: 12 }} />
    </button>
  );
}

/** Miniature du modèle (rendu une fois, cf. AvatarRenderer.getModelThumbnail) ; tant
 * qu'elle n'est pas prête, ou si elle échoue, l'icône générique reste affichée. */
function ModelThumbnail({ model }: { model: AvatarModelDef }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getModelThumbnail(model).then(url => { if (!cancelled) setSrc(url); });
    return () => { cancelled = true; };
  }, [model]);
  if (!src) return null;
  return <img src={src} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#0f172a' }} />;
}

function renderModelIcon(model: AvatarModelDef) {
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <ModelThumbnail model={model} />
      {model.icon === 'fish' && <Fish style={{ width: 26, height: 26, color: '#94a3b8' }} />}
      {model.icon === 'crown' && <Crown style={{ width: 26, height: 26, color: '#94a3b8' }} />}
      {model.icon === 'glasses' && <Glasses style={{ width: 26, height: 26, color: '#94a3b8' }} />}
      {model.icon === 'ring' && <Gem style={{ width: 26, height: 26, color: '#94a3b8' }} />}
      {model.icon === 'face' && <Smile style={{ width: 26, height: 26, color: '#94a3b8' }} />}
      {model.icon === 'hand' && <Hand style={{ width: 26, height: 26, color: '#94a3b8' }} />}
      {model.icon === 'generic' && <span style={{ fontSize: 9, color: 'rgba(148,163,184,0.5)', textAlign: 'center', padding: 4 }}>{model.label}</span>}
    </div>
  );
}

/** Une tuile "Aucun" en tête de grille — désactive cette famille (visage ou mains)
 * sans toucher à l'autre (cf. AvatarConfig.faceModelId/leftHandModelId/rightHandModelId, indépendants). */
function NoneTile({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <ThumbTile active={active} label="Aucun" onClick={onClick}>
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(148,163,184,0.5)', fontSize: 9 }}>
        Aucun
      </div>
    </ThumbTile>
  );
}

/** Petit groupe de choix en pilules (main/doigt de la Bague) — plus léger qu'une
 * grille de ThumbTile pour un réglage texte-seulement. */
function PillChoice<T extends string>({ options, value, onChange }: { options: { id: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(opt => (
        <button
          key={opt.id} type="button" onClick={() => onChange(opt.id)}
          style={{
            padding: '4px 10px', borderRadius: 999, fontSize: 11, cursor: 'pointer',
            border: opt.id === value ? '1px solid #38bdf8' : '1px solid #475569',
            background: opt.id === value ? 'rgba(56,189,248,0.15)' : 'rgba(255,255,255,0.03)',
            color: opt.id === value ? '#38bdf8' : '#94a3b8',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** Grille de choix d'UNE main (gauche ou droite) : « Aucun », les modèles de la famille
 * Mains, la tuile « + ». Chaque modèle est réservé au côté où il a été
 * ajouté ; ceux d'avant la distinction apparaissent des deux côtés. */
function HandPicker({ models, side, value, onPick }: { models: AvatarModelDef[]; side: 'left' | 'right'; value: string | null; onPick: (id: string | null) => void }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
      <NoneTile active={!value} onClick={() => onPick(null)} />
      {models.filter(m => !m.handSide || m.handSide === side).map(model => (
        <ThumbTile key={model.id} active={value === model.id} label={model.label}
          onClick={() => onPick(model.id)}
          corner={<DeleteCorner model={model} onDeleted={() => { if (value === model.id) onPick(null); }} />}>
          {renderModelIcon(model)}
        </ThumbTile>
      ))}
      <AddTile family="hand" side={side} />
    </div>
  );
}

/** Grille "Décoration" — cumulable, avec une icône ⚙ par tuile pour ouvrir le
 * panneau de calibration (cf. DecorationCalibrationModal) de CETTE décoration.
 * Composant à part (et non inline dans renderControls, une simple méthode de
 * classe) : il lui faut son propre état React (quel panneau est ouvert). */
const DecorationSection: React.FC<{ config: AvatarConfig; onChange: (config: AvatarConfig) => void }> = ({ config, onChange }) => {
  const { decorations: DECORATIONS } = useAvatarModelLists();
  const [calibratingId, setCalibratingId] = useState<string | null>(null);
  const calibrating = calibratingId ? getAvatarModelDef(calibratingId) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>Décoration</span>
      <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)', lineHeight: 1.4 }}>
        Cumulables entre elles ET avec Visage/Mains — plusieurs à la fois si tu veux.
        L'icône ⚙ ouvre le réglage de position/rotation/taille.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        {DECORATIONS.map(model => {
          const active = config.decorationIds.includes(model.id);
          return (
            <ThumbTile key={model.id} active={active} label={model.label}
              onClick={() => onChange({
                ...config,
                decorationIds: active
                  ? config.decorationIds.filter(id => id !== model.id)
                  : [...config.decorationIds, model.id],
              })}
              corner={
                <>
                <DeleteCorner model={model} onDeleted={() => onChange({
                  ...config,
                  decorationIds: config.decorationIds.filter(id => id !== model.id),
                })} />
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); setCalibratingId(model.id); }}
                  title={`Calibrer ${model.label}`}
                  style={{
                    position: 'absolute', top: 2, right: 2, width: 20, height: 20, padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 6, border: '1px solid rgba(148,163,184,0.4)',
                    background: 'rgba(15,23,42,0.75)', color: '#cbd5e1', cursor: 'pointer',
                  }}
                >
                  <Settings style={{ width: 12, height: 12 }} />
                </button>
                </>
              }
            >
              {renderModelIcon(model)}
            </ThumbTile>
          );
        })}
        <AddTile family="decoration" />
      </div>

      {calibrating && (
        <DecorationCalibrationModal
          decoration={calibrating}
          // Ancrage 'hand' → réplique le modèle actuellement choisi dans "Mains",
          // sinon celui choisi dans "Visage" (cf. AvatarConfig.faceModelId/leftHandModelId/rightHandModelId)
          // — demandé explicitement : calibrer sur LE modèle réellement utilisé en
          // régie, pas une forme générique de substitution.
          referenceModel={calibrating.anchor === 'hand' ? getAvatarModelDef(config.leftHandModelId ?? config.rightHandModelId) : getAvatarModelDef(config.faceModelId)}
          initialTransform={config.decorationTransforms[calibrating.id] ?? calibrating.defaultTransform ?? DEFAULT_DECORATION_TRANSFORM}
          onClose={() => setCalibratingId(null)}
          onSave={transform => {
            onChange({ ...config, decorationTransforms: { ...config.decorationTransforms, [calibrating.id]: transform } });
            setCalibratingId(null);
          }}
        />
      )}
    </div>
  );
};

const RING_HAND_OPTIONS: { id: AvatarConfig['ringHand']; label: string }[] = [
  { id: 'both', label: 'Les deux mains' },
  { id: 'left', label: 'Gauche' },
  { id: 'right', label: 'Droite' },
];
const RING_FINGER_OPTIONS: { id: FingerName; label: string }[] = [
  { id: 'thumb', label: 'Pouce' },
  { id: 'index', label: 'Index' },
  { id: 'middle', label: 'Majeur' },
  { id: 'ring', label: 'Annulaire' },
  { id: 'pinky', label: 'Auriculaire' },
];

// Modèle 3D (cf. AvatarModels.ts pour la liste, extensible) ancré au visage suivi par
// FaceTracker.ts ET/OU à chaque main suivie par HandTracker.ts — visage et mains sont
// deux familles INDÉPENDANTES et CUMULABLES (deux sélecteurs séparés ci-dessous,
// chacun avec sa propre échelle) : on peut n'utiliser que l'un, que l'autre, ou les
// deux à la fois. Tant qu'un modèle choisi n'a pas fini de charger (une seule fois
// par modèle et par session), on retombe sur le nuage de points — jamais un cadre
// vide pendant le chargement.
class AvatarEffectImpl extends VideoEffect<AvatarConfig> {
  readonly id = 'avatar';
  readonly label = 'Avatar';
  readonly icon = UserCircle2;
  readonly defaultConfig: AvatarConfig = {
    faceModelId: null, faceScale: 1, leftHandModelId: null, rightHandModelId: null, handScale: 1, hideBody: false,
    decorationIds: [], decorationTransforms: {}, ringHand: 'both', ringFinger: 'ring',
  };
  readonly group = 'face' as const;
  readonly needsFaceTracking = true;
  readonly needsHandTracking = true;
  // Toujours actif tant qu'Avatar est actif, que "Cacher le corps" soit coché ou non
  // (cf. VideoEffect.needsSegmentation : un simple booléen par effet, pas par config
  // — impossible de le rendre conditionnel à ce stade du pipeline).
  readonly needsSegmentation = true;

  // Le détourage ne sert qu'à « Cacher le corps » (cf. wantsPersonHidden et compositeFrame :
  // `hidePerson` exige un masque ET hideBody). Sans lui, la segmentation 1080p tournait à
  // chaque image pour rien — l'une des trois tâches MediaPipe qui saturaient le thread
  // principal (25/10/2026). `needsSegmentation` reste vrai pour l'aperçu du réglage de source.
  needsSegmentationFor(config: AvatarConfig): boolean {
    return config.hideBody;
  }

  // "Cacher le corps" — cf. compositeFrame.ts : ça dit à l'effet "surroundings" actif
  // (Arrière-plan) de NE PAS coller la personne par-dessus son fond, pour que ce fond
  // apparaisse entier à la place du corps plutôt que la personne réelle. Le calque
  // Avatar (ci-dessous) n'a lui-même rien de spécial à faire pour ça — la personne
  // est déjà absente de la base par le temps que son composite() ci-dessous s'exécute.
  wantsPersonHidden(config: AvatarConfig): boolean {
    return config.hideBody;
  }

  composite({ ctx, width, height, frame, faceLandmarks, handLandmarks, config }: VideoEffectCompositeArgs<AvatarConfig>) {
    // PAS de sortie anticipée si les deux familles sont vides pour CETTE frame : le
    // renderer peut tenir une pose détectée récemment mais absente de cette frame
    // précise (rotation trop marquée pour MediaPipe, cf. AvatarRenderer.ANCHOR_HOLD_MS)
    // — c'est lui qui décide, avec sa mémoire des frames précédentes, s'il y a
    // vraiment plus rien à afficher pour une famille donnée.
    // Réglages fins par décoration ancrée 'hand' — seule "Bague" en a pour l'instant
    // (cf. AvatarConfig.ringHand/ringFinger), mais le mécanisme (AvatarRenderer.
    // HandDecorationOptions) est générique : une future décoration pourrait s'y
    // ajouter sans toucher au renderer.
    const handOptionsById = new Map<string, HandDecorationOptions>();
    if (config.decorationIds.includes('ring')) {
      handOptionsById.set('ring', { handSide: config.ringHand, fingerLandmarkIndex: FINGER_PIP_LANDMARK[config.ringFinger] });
    }

    const decorationTransformsById = new Map<string, DecorationTransform>(Object.entries(config.decorationTransforms));

    const result = getAvatarRenderer(frame).render(
      faceLandmarks, handLandmarks, width, height,
      config.faceModelId, config.faceScale,
      config.leftHandModelId, config.rightHandModelId, config.handScale,
      config.decorationIds, handOptionsById, decorationTransformsById,
    );

    // Repli points de repère pour UNE famille (visage OU mains) : dessine pour les
    // seuls index listés dans `pending`, à partir du tableau brut `anchors` de cette
    // frame — un index tenu en mémoire par le renderer mais absent de ce tableau
    // n'a simplement rien à dessiner ici (déjà réglé par AvatarRenderer.render, ces
    // index-là ne remontent jamais comme "pending").
    const drawFallbackDots = (anchors: { landmarks: { x: number; y: number }[] }[], pending: number[]) => {
      if (pending.length === 0) return;
      ctx.save();
      ctx.fillStyle = '#34d399';
      for (const i of pending) {
        const anchor = anchors[i];
        if (!anchor) continue;
        for (const p of anchor.landmarks) { ctx.beginPath(); ctx.arc(p.x * width, p.y * height, 1.5, 0, Math.PI * 2); ctx.fill(); }
      }
      ctx.restore();
    };

    if (!result) {
      // Renderer WebGL carrément indisponible — repli points de repère pour tout ce
      // que CETTE frame a réellement détecté dans les trois familles (pas de mémoire
      // à offrir sans lui).
      if (config.faceModelId) drawFallbackDots(faceLandmarks, faceLandmarks.map((_, i) => i));
      if (config.leftHandModelId || config.rightHandModelId) drawFallbackDots(handLandmarks, handLandmarks.map((_, i) => i));
      for (const id of config.decorationIds) {
        const anchors = getAvatarModelDef(id)?.anchor === 'hand' ? handLandmarks : faceLandmarks;
        drawFallbackDots(anchors, anchors.map((_, i) => i));
      }
      return;
    }

    ctx.drawImage(result.canvas, 0, 0, width, height);
    drawFallbackDots(faceLandmarks, result.facePendingIndices);
    drawFallbackDots(handLandmarks, result.handPendingIndices);
    // .forEach (pas for...of) : la cible ES5 du projet n'itère pas nativement une
    // Map sans downlevelIteration.
    result.decorationPendingIndices.forEach((pending, id) => {
      const anchors = getAvatarModelDef(id)?.anchor === 'hand' ? handLandmarks : faceLandmarks;
      drawFallbackDots(anchors, pending);
    });
  }

  renderControls({ config, onChange }: VideoEffectControlProps<AvatarConfig>) {
    return <AvatarControls config={config} onChange={onChange} />;
  }
}

// Corps du panneau — composant à part pour pouvoir s'abonner à la liste des modèles
// (hook), ce que renderControls, simple méthode de classe, ne peut pas faire.
const AvatarControls: React.FC<{ config: AvatarConfig; onChange: (config: AvatarConfig) => void }> = ({ config, onChange }) => {
  const { faceModels: FACE_MODELS, handModels: HAND_MODELS } = useAvatarModelLists();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)', lineHeight: 1.5 }}>
        Modèle 3D pour le visage et/ou pour les mains — indépendants, cumulables :
        active l'un, l'autre, ou les deux à la fois.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>Visage</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          <NoneTile active={!config.faceModelId} onClick={() => onChange({ ...config, faceModelId: null })} />
          {FACE_MODELS.map(model => (
            <ThumbTile key={model.id} active={config.faceModelId === model.id} label={model.label}
              onClick={() => onChange({ ...config, faceModelId: model.id })}
              corner={<DeleteCorner model={model} onDeleted={() => { if (config.faceModelId === model.id) onChange({ ...config, faceModelId: null }); }} />}>
              {renderModelIcon(model)}
            </ThumbTile>
          ))}
          <AddTile family="face" />
        </div>
        {config.faceModelId && (
          <LevelSlider
            label="Taille (visage)" value={config.faceScale}
            min={0.3} max={3} defaultValue={1} roundTo={0.1}
            format={v => `${v.toFixed(1)}×`}
            onChange={faceScale => onChange({ ...config, faceScale })}
          />
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>Main gauche</span>
        <HandPicker models={HAND_MODELS} side="left" value={config.leftHandModelId} onPick={leftHandModelId => onChange({ ...config, leftHandModelId })} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>Main droite</span>
        <HandPicker models={HAND_MODELS} side="right" value={config.rightHandModelId} onPick={rightHandModelId => onChange({ ...config, rightHandModelId })} />
        {(config.leftHandModelId || config.rightHandModelId) && (
          <LevelSlider
            label="Taille (mains)" value={config.handScale}
            min={0.3} max={3} defaultValue={1} roundTo={0.1}
            format={v => `${v.toFixed(1)}×`}
            onChange={handScale => onChange({ ...config, handScale })}
          />
        )}
      </div>

      <DecorationSection config={config} onChange={onChange} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {config.decorationIds.includes('ring') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            <div>
              <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)', marginBottom: 4 }}>Bague — main</div>
              <PillChoice options={RING_HAND_OPTIONS} value={config.ringHand} onChange={ringHand => onChange({ ...config, ringHand })} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)', marginBottom: 4 }}>Bague — doigt</div>
              <PillChoice options={RING_FINGER_OPTIONS} value={config.ringFinger} onChange={ringFinger => onChange({ ...config, ringFinger })} />
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 13, color: '#e2e8f0' }}>Cacher le corps</span>
        <Toggle
          form="switch" checked={config.hideBody}
          onChange={hideBody => onChange({ ...config, hideBody })}
        />
      </div>
    </div>
  );
};

export const avatarEffect = new AvatarEffectImpl();
